import React, { useState, useRef, useEffect } from 'react';
import { DivideIcon as LucideIcon } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  icon?: LucideIcon;
  error?: string;
  helper?: string;
  defaultValue?: string | number;
  placeholder?: string;
  autoClear?: boolean;
  onValueChange?: (value: string) => void;
}

export const Input: React.FC<InputProps> = ({
  label,
  icon: Icon,
  error,
  helper,
  className = '',
  defaultValue = '',
  placeholder,
  autoClear = true,
  onChange,
  onBlur,
  onValueChange,
  ...props
}) => {
  const [value, setValue] = useState(() => {
    if (defaultValue === 0 || defaultValue === '0') return '';
    return defaultValue.toString();
  });
  
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasInteracted && defaultValue !== undefined) {
      if (defaultValue === 0 || defaultValue === '0') {
        setValue('');
      } else {
        setValue(defaultValue.toString());
      }
    }
  }, [defaultValue, hasInteracted]);

  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const unformatNumber = (str: string): string => {
    return str.replace(/,/g, '');
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (value === '0') {
      setValue('');
      setHasInteracted(true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    
    if (props.type === 'number') {
      newValue = newValue.replace(/[^\d.-]/g, '');
      
      const decimalCount = (newValue.match(/\./g) || []).length;
      if (decimalCount > 1) return;
      
      if (!props.min || Number(props.min) < 0) {
        if (!/^-?\d*\.?\d*$/.test(newValue)) return;
      } else {
        if (!/^\d*\.?\d*$/.test(newValue)) return;
      }
    }

    setValue(newValue);
    setHasInteracted(true);
    onChange?.(e);
    onValueChange?.(newValue);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    
    if (value === '' || value === '-' || value === '.') {
      setValue('');
      onValueChange?.('');
    } else if (props.type === 'number' && value !== '') {
      const num = parseFloat(unformatNumber(value));
      if (!isNaN(num)) {
        const formatted = formatNumber(num);
        setValue(formatted);
        onValueChange?.(formatted);
      }
    }
    
    onBlur?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      if (value === '' || value === '-' || value === '.') {
        setValue('');
        onValueChange?.('');
      }
    } else if (e.key === 'Escape') {
      inputRef.current?.blur();
    }
  };

  return (
    <div className="space-y-2" ref={containerRef}>
      {label && (
        <label 
          htmlFor={props.id} 
          className={`block text-sm transition-colors ${
            isFocused ? 'text-emerald-500' : 'text-slate-400'
          }`}
        >
          {label}
        </label>
      )}
      <div className="relative">
        {Icon && (
          <Icon 
            className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors ${
              isFocused ? 'text-emerald-500' : 'text-slate-400'
            }`}
          />
        )}
        <input
          ref={inputRef}
          id={props.id}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`glass-input ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-2 w-full rounded-lg transition-all
            ${error ? 'border-red-500 focus:border-red-500' : 'focus:border-emerald-500'}
            ${isFocused ? 'ring-1 ring-emerald-500/20' : ''}
            ${className}`}
          placeholder={placeholder}
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontFeatureSettings: '"tnum"',
            letterSpacing: '0.01em'
          }}
          {...props}
        />
      </div>
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
      {helper && !error && (
        <p className="text-sm text-slate-400">{helper}</p>
      )}
    </div>
  );
};